import { useRequestFeedback } from '@reflag/react-sdk';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    confirm,
    Menu,
    MenuItem,
    MenuRoot,
    MenuSection,
    MenuTrigger,
    Popover,
} from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import { useCurrentOrganization } from '../../state';

/**
 * Options menu in a conversation's header, wherever that conversation is being read.
 *
 * Holds what the header used to spend width on before the dock: configuring the agent and sending
 * feedback. Deleting is only offered once the conversation has been started — a draft has nothing
 * to delete, and closing its tab already discards it.
 */
export function AgentDockConversationMenu(props: {
    /** Omitted for a draft, which cannot be deleted because it does not exist yet. */
    onDelete?: () => Promise<void> | void;
}) {
    const { onDelete } = props;
    const organization = useCurrentOrganization();
    const { navigate } = useNavigation();
    const requestFeedback = useRequestFeedback();

    const onPressDelete = () => {
        void confirm({
            title: 'Delete conversation',
            description: (
                <>This will permanently delete this conversation and all of its messages.</>
            ),
            kind: 'danger',
            confirmLabel: 'Delete',
            onConfirm: () => onDelete?.(),
        });
    };

    return (
        <MenuRoot>
            <MenuTrigger>
                <Button
                    kind="blank"
                    size="small"
                    leadingIcon={Icon.Options02}
                    label="Conversation options"
                    iconOnly
                />
            </MenuTrigger>
            <Popover>
                <Menu>
                    {organization.permissions.admin ? (
                        <MenuSection key="settings">
                            <MenuItem
                                icon={Icon.Settings}
                                onAction={() =>
                                    navigate({
                                        screen: {
                                            type: 'org_settings',
                                            organization: organization.id,
                                            tab: { type: 'agents' },
                                        },
                                    })
                                }
                            >
                                Configure GitBook Agent
                            </MenuItem>
                        </MenuSection>
                    ) : null}
                    <MenuSection key="feedback">
                        <MenuItem
                            icon={Icon.Happy}
                            onAction={() =>
                                requestFeedback({
                                    flagKey: 'DOCS_AGENTS',
                                    title: 'How satisfied are you with using GitBook Agent?',
                                    openWithCommentVisible: true,
                                })
                            }
                        >
                            Share feedback
                        </MenuItem>
                    </MenuSection>
                    {onDelete ? (
                        <MenuSection key="delete">
                            <MenuItem icon={Icon.Trashcan} onAction={onPressDelete}>
                                Delete conversation
                            </MenuItem>
                        </MenuSection>
                    ) : null}
                </Menu>
            </Popover>
        </MenuRoot>
    );
}
